import { Link } from "react-router-dom";
import { movies } from "@/mocks/movies";
import { newsList } from "@/mocks/news";
import { usePageMeta } from "@/hooks/usePageMeta";
import SectionHeading from "@/components/feature/SectionHeading";
import MovieCard from "@/components/feature/MovieCard";
import NewsCard from "@/components/feature/NewsCard";
import HeroCarousel from "./components/HeroCarousel";
import CategoryEntries from "./components/CategoryEntries";

const latestMovies = movies.slice(0, 8);
const latestNews = newsList.slice(0, 5);

export default function Home() {
  usePageMeta({
    title: "国产精品久久－优质国产影视资源与最新资讯平台",
    description:
      "国产精品久久为您提供丰富的国产影视资源和最新影视资讯，涵盖电影、电视剧及热门影视作品，帮助用户轻松查找并了解优质国产影视内容。",
    keywords: "国产精品久久,国产影视,国产电影,国产电视剧,影视资讯,国产剧推荐,影视资源",
  });

  return (
    <div>
      <h1 className="sr-only">国产精品久久</h1>

      <HeroCarousel />

      <div className="mx-auto w-full px-4 md:px-8">
        <section className="mt-12">
          <SectionHeading title="分类快捷入口" subtitle="按频道快速找到你想看的国产影视" />
          <CategoryEntries />
        </section>

        <section className="mt-16">
          <SectionHeading
            title="最新上映影视"
            subtitle="近期热门与口碑佳作合集"
            moreTo="/category/all"
            moreLabel="全部影视"
          />
          <div className="grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-4">
            {latestMovies.map((movie) => (
              <MovieCard key={movie.id} movie={movie} />
            ))}
          </div>
        </section>

        <section className="mt-16">
          <SectionHeading
            title="最新影视资讯"
            subtitle="影视动态、行业报道与预告速递"
            moreTo="/news"
            moreLabel="更多资讯"
          />
          <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
            <div className="flex flex-col gap-4 lg:col-span-2">
              {latestNews.map((article) => (
                <NewsCard key={article.id} article={article} variant="list" />
              ))}
            </div>
            <aside className="rounded-lg border border-background-200 bg-background-100 p-5">
              <h3 className="flex items-center gap-2 font-heading text-base font-bold text-foreground-950">
                <i className="ri-price-tag-3-line text-accent-500" />
                热门标签
              </h3>
              <div className="mt-4 flex flex-wrap gap-2">
                {["现实题材", "悬疑", "科幻", "古装", "家庭", "犯罪", "武侠", "都市", "谍战", "年代"].map(
                  (tag) => (
                    <Link
                      key={tag}
                      to={`/search?q=${encodeURIComponent(tag)}`}
                      className="rounded-full bg-background-200 px-3 py-1.5 text-xs text-foreground-700 transition-colors hover:bg-primary-100 hover:text-primary-700"
                    >
                      {tag}
                    </Link>
                  )
                )}
              </div>
              <div className="mt-6 rounded-md bg-accent-100 p-4">
                <p className="flex items-center gap-2 text-sm font-semibold text-accent-900">
                  <i className="ri-fire-line" />
                  本周最热
                </p>
                <p className="mt-2 text-xs leading-relaxed text-accent-900/80">
                  悬疑剧《雾隐迷城》定档消息引发热议，登顶本周资讯热度榜。
                </p>
                <Link
                  to="/news/gp9006"
                  className="mt-3 inline-flex items-center gap-1 text-xs font-medium text-accent-900 hover:underline"
                >
                  查看详情
                  <i className="ri-arrow-right-line" />
                </Link>
              </div>
            </aside>
          </div>
        </section>

        <section className="mt-16 overflow-hidden rounded-lg border border-background-200 bg-secondary-100">
          <div className="grid grid-cols-1 lg:grid-cols-2">
            <div className="flex flex-col justify-center p-6 md:p-10">
              <span className="text-xs font-semibold uppercase tracking-widest text-primary-600">
                ABOUT US
              </span>
              <h2 className="mt-3 font-heading text-2xl font-bold text-foreground-950 md:text-3xl">
                关于国产精品久久
              </h2>
              <p className="mt-4 text-sm leading-relaxed text-foreground-700 md:text-base">
                国产精品久久是一个专注国产电影、电视剧等优质影视资源与最新影视资讯的综合平台。
                我们汇聚丰富的影视资料，通过清晰的分类与筛选，帮助每一位影迷便捷地浏览作品、
                获取最新动态，并发现那些值得被看见的国产佳作。
              </p>
              <div className="mt-6 flex flex-wrap gap-6">
                <div>
                  <p className="font-heading text-2xl font-bold text-primary-600">5000+</p>
                  <p className="mt-1 text-xs text-foreground-600">收录影视作品</p>
                </div>
                <div>
                  <p className="font-heading text-2xl font-bold text-primary-600">1000+</p>
                  <p className="mt-1 text-xs text-foreground-600">影视资讯报道</p>
                </div>
                <div>
                  <p className="font-heading text-2xl font-bold text-primary-600">每日</p>
                  <p className="mt-1 text-xs text-foreground-600">更新影视动态</p>
                </div>
              </div>
              <Link
                to="/about"
                className="mt-8 flex w-fit items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-6 py-3 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
              >
                了解更多
                <i className="ri-arrow-right-line" />
              </Link>
            </div>
            <div className="min-h-[220px] w-full lg:min-h-[360px]">
              <img
                src="https://readdy.ai/api/search-image?query=Warm%20cozy%20home%20cinema%20scene%20with%20large%20screen%20glowing%20in%20a%20dim%20room%2C%20comfortable%20sofa%20and%20popcorn%2C%20amber%20and%20deep%20red%20tones%2C%20inviting%20editorial%20atmosphere%2C%20soft%20lighting%2C%20wide%20composition&width=900&height=700&seq=gp-about-01&orientation=landscape"
                alt="国产精品久久 影视内容平台"
                title="国产精品久久 - 国产影视内容平台"
                className="h-full w-full object-cover object-top"
              />
            </div>
          </div>
        </section>
      </div>
    </div>
  );
}